import { FC, useEffect, useState } from "react";
import { ErrorMessage, Input } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import { useSearchParams, Link } from "react-router-dom";
import authService from "@/services/authService";
import ROUTES from "@/data/Routes";
import Container from "@/components/Container";
import { Spinner } from "@/components/UI/Spinner";
import { useFormik } from "formik";
import { z } from "zod";
import { toFormikValidate } from "zod-formik-adapter";
import { REQUIRED_FIELDS_ERROR } from "@/utils/formValidator";

const forgotSchema = z.object({
  email: z.string().email("El. pašto formatas netinkamas"),
});

const ForgotPassword: FC = () => {
  const [searchParams] = useSearchParams();
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [successMsg, setSuccessMsg] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: searchParams.get("email") ?? "",
    },
    validate: toFormikValidate(forgotSchema),
    onSubmit: async (values) => {
      setErrorMsg(undefined);
      setIsLoading(true);
      try {
        await authService.requestPasswordReset(values.email);
        setSuccessMsg(
          "Slaptažodžio atkūrimo nuoroda išsiųsta jūsų el. paštu. Patikrinkite savo el. pašto dėžutę.",
        );
      } catch (error: any) {
        setErrorMsg(
          error.response?.data?.message || "Klaida siunčiant el. laišką",
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      formik.setFieldValue("email", emailParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) {
      setErrorMsg(REQUIRED_FIELDS_ERROR);
      return;
    }
    await formik.handleSubmit(e as React.FormEvent<HTMLFormElement>);
  };

  return (
    <section className="bg-canvas px-4 py-14 md:px-6 md:py-20">
      <Container className="flex items-center justify-center pt-15">
        <div className="text-grey mx-auto w-full max-w-[620px]">
          <div className="mb-8 flex flex-col gap-3 text-center">
            <h1 className="text-ink text-[30px] leading-[1.15] font-medium md:text-[34px]">
              Pamiršau slaptažodį
            </h1>
            <p className="text-muted mx-auto max-w-[460px] text-sm leading-[1.5] md:text-base">
              Įveskite savo el. paštą ir atsiųsime jums slaptažodžio atkūrimo
              nuorodą.
            </p>
          </div>

          {successMsg ? (
            <div className="flex flex-col gap-6 text-center">
              <p className="rounded-[18px] border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                {successMsg}
              </p>
              <Link
                to={ROUTES.LOGIN}
                className="text-muted hover:text-ink text-sm underline underline-offset-4 transition-colors"
              >
                Grįžti į prisijungimo puslapį
              </Link>
            </div>
          ) : (
            <form
              className="flex w-full flex-col gap-7"
              onSubmit={handleSubmit}
              noValidate
            >
              <ErrorMessage error={errorMsg} />
              <div className="flex w-full flex-col gap-[18px]">
                <Input
                  label="El. paštas"
                  type="email"
                  name="email"
                  value={formik.values.email}
                  autoComplete="email"
                  required
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="flex flex-col gap-4">
                <Button
                  variant="ink"
                  disabled={isLoading}
                  className="h-[63px]"
                  onHover
                  hoverGradientLight
                >
                  Siųsti atkūrimo nuorodą
                </Button>
                <Link
                  to={ROUTES.LOGIN}
                  className="text-muted hover:text-ink mx-auto text-sm underline underline-offset-4 transition-colors"
                >
                  Grįžti į prisijungimą
                </Link>
              </div>
            </form>
          )}

          {isLoading && !successMsg && (
            <div className="mt-6 flex justify-center">
              <Spinner className="text-primary size-10" />
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default ForgotPassword;
